<template>
  <div class="modern-form">
    <div class="form-container">
      <!-- Insurance Title -->
      <div class="section-header">
        <h2 class="section-title">Insurance</h2>
      </div>

      <div v-if="this.patient" class="patient-header">
        {{ patient.lastName }}, {{ patient.firstName }} ({{patient.birthDate }})
      </div>

      <v-form ref="form" v-if="this.patient">
        <div class="form-section">
          <div class="question-row">
            <h4 class="question-title">Do you have Insurance?</h4>
            <div class="modern-toggle">
              <button 
                type="button"
                class="toggle-option"
                :class="{ active: form.haveInsurance === false }"
                @click="form.haveInsurance = false">
                No
              </button>
              <button 
                type="button"
                class="toggle-option"
                :class="{ active: form.haveInsurance === true }"
                @click="form.haveInsurance = true">
                Yes
              </button>
            </div>
          </div>
        </div>
        <div v-if="form.haveInsurance" class="form-section">
          <p class="info-text">
            To change insurance, or to add a secondary insurance provider, please contact office number {{ officePhone }}.
          </p>
          
          <h4 class="section-subtitle">Upload Dental Insurance Documents</h4>
          <div class="upload-section">
            <div class="upload-item">
              <v-file-input v-show="false"
                ref="fileFront"
                @change="previewAndConvert('front')"
                v-model="imageFront">
              </v-file-input>
              <v-btn class="btn-secondary upload-btn" @click="click('front')">
                <v-icon class="mr-2">mdi-upload</v-icon>
                <span class="upload-text-desktop">Upload Dental Insurance Card (Front)</span>
                <span class="upload-text-mobile">Upload Card (Front)</span>
              </v-btn>
              <v-img v-if="urlFront" :src="urlFront" class="upload-preview"></v-img>
            </div>
            
            <div class="upload-item">
              <v-file-input v-show="false"
                ref="fileBack"
                @change="previewAndConvert('back')"
                v-model="imageBack">
              </v-file-input>
              <v-btn class="btn-secondary upload-btn" @click="click('back')">
                <v-icon class="mr-2">mdi-upload</v-icon>
                <span class="upload-text-desktop">Upload Dental Insurance Card (Back)</span>
                <span class="upload-text-mobile">Upload Card (Back)</span>
              </v-btn>
              <v-img v-if="urlBack" :src="urlBack" class="upload-preview"></v-img>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-row">
              <div class="form-group">
                <v-select
                  v-model="form.policyHolderRelationshipToPatient"
                  :items="relationshipToPatientItems"
                  :rules="[rules.required]"
                  label="Policy Holder Relationship to Patient *"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
              
              <div class="form-group">
                <LessBadBirthDatePicker
                  v-model="form.policyHolderBirthDate"
                  :rules="[rules.required]"
                  label="Policy Holder Birth Date *"
                  class="line-input"
                  dense />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <v-text-field
                  v-model="form.subscriberId"
                  :rules="[rules.required, rules.max25]"
                  label="Subscriber ID *"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
              
              <div class="form-group">
                <v-text-field
                  v-model="form.groupNumber"
                  label="Group Number"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <v-text-field
                  v-model="form.policyHolderSocialSecurityNumber"
                  label="Policy Holder Social Security Number"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
              
              <div class="form-group">
                <v-text-field
                  v-model="form.firstNameOfPolicyHolder"
                  :rules="[rules.required, rules.max50]"
                  label="First Name of Policy Holder *"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <v-text-field
                  v-model="form.lastNameOfPolicyHolder"
                  :rules="[rules.required, rules.max50]"
                  label="Last Name of Policy Holder *"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
              
              <div class="form-group">
                <v-text-field
                  v-model="form.employerName"
                  label="Employer Name"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <v-text-field
                  v-model="form.insuranceCompany"
                  :rules="[rules.required]"
                  label="Insurance Company *"
                  class="line-input custom-filled"
                  dense
                  hide-details="auto" />
              </div>
            </div>
          </div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import { heicTo, isHeic } from 'heic-to';
import { jsPDF } from 'jspdf';
import DataService from '../../../_services/DataService';
import ValidateService from '../../../_services/ValidateService';
import Utils from '../../utils';
import LessBadBirthDatePicker from '../LessBadBirthDatePicker';

export default {
  name: 'InsuranceForm',
  props: ['patient'],
  components: {
    LessBadBirthDatePicker,
  },
  data() {
    return {
      officePhone: '',
      form: {
        id: 2,
        haveInsurance: true,
        // upload document buttons
        policyHolderRelationshipToPatient: '',
        policyHolderBirthDate: '',
        subscriberId: '',
        groupNumber: '',
        policyHolderSocialSecurityNumber: '',
        firstNameOfPolicyHolder: '',
        lastNameOfPolicyHolder: '',
        employerName: '',
        insuranceCompany: '',
      },
      relationshipToPatientItems: ['Self', 'Spouse', 'Parent / Guardian'],
      rules: ValidateService.validators,
      urlFront: null,
      urlBack: null,
      imageFront: null,
      imageBack: null,
      frontBase64: '',
      backBase64: '',
    };
  },
  methods: {
    click(side) {
      if (side == 'front') {
        this.$refs.fileFront.$refs.input.click();
      } else if (side == 'back') {
        this.$refs.fileBack.$refs.input.click();
      }
    },
    previewAndConvert(side) {
      const image = side === 'front' ? this.imageFront : this.imageBack;

      // generate base64
      return new Promise((resolve, reject) => {
        isHeic(image).then(async (isHeicFormat) => {
          const normalizedImage = isHeicFormat ? await heicTo({ blob: image, type: 'image/jpeg', quality: 0.8 }) : image;

          if (side === 'front') {
            this.urlFront = URL.createObjectURL(normalizedImage);
          } else if (side === 'back') {
            this.urlBack = URL.createObjectURL(normalizedImage);
          }

          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result);
            if (side === 'front') {
              this.frontBase64 = reader.result;
            } else if (side === 'back') {
              this.backBase64 = reader.result;
            }
          };
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(normalizedImage);
        });
      });
    },
    async validate() {
      return this.$refs.form.validate();
    },
    async sendForm(base64PDF) {
      this.form.base64PDF = base64PDF;

      const response = await DataService.postNewPatRegistration(this.form, this.patient.id);
      if (response.status === 200) {
        this.$emit('goToNextForm');
        this.$root.$emit('raise_alert', { color: 'success', text: 'Your entry has been recorded.' });
      } else {
        this.$emit('submitFailure');
        this.$root.$emit('raise_alert', { color: 'error', text: 'We were unable to save your insurance info, please try again or contact our team if the issue persists' });
      }
    },
    async generatePDF() {
      const doc = new jsPDF();
      doc.setFontSize(10).text(10, 20, `Do you have Insurance: ${this.form.haveInsurance == true ? 'Yes' : 'No'}`).setFont(undefined, 'bold');
      if (this.form.haveInsurance){
        doc.text(10, 25, 'Insurance').setFont(undefined, 'normal');
        doc.text(10, 30, `Policy Holder Relationship to Patient: ${this.form.policyHolderRelationshipToPatient || ''}`);
        doc.text(10, 35, `Policy Holder Birth Date: ${this.form.policyHolderBirthDate || ''}`);
        doc.text(10, 40, `Subscriber ID: ${this.form.subscriberId || ''}`);
        doc.text(10, 45, `Group Number: ${this.form.groupNumber || ''}`);
        doc.text(10, 50, `Policy Holder Social Security Number: ${this.form.policyHolderSocialSecurityNumber || ''}`);
        doc.text(10, 55, `First Name of Policy Holder: ${this.form.firstNameOfPolicyHolder}`);
        doc.text(10, 60, `Last Name of Policy Holder: ${this.form.lastNameOfPolicyHolder || ''}`);
        doc.text(10, 65, `Employer Name: ${this.form.employerName || ''}`);
        doc.text(10, 70, `Insurance Company: ${this.form.insuranceCompany || ''}`);
        doc.text(10, 75, `Date Signed: ${new Date().toISOString()}`);

        if (this.frontBase64) {
          doc.addImage(this.frontBase64, 'JPEG', 10, 80, 140, 80, undefined, 'FAST');
        }
        if (this.backBase64) {
          doc.addImage(this.backBase64, 'JPEG', 10, 165, 140, 80, undefined, 'FAST');
        }
      }

      await this.sendForm(doc.output('datauristring').split(',')[1]);
    },
  },
  async mounted() {
    const { phone } = await DataService.getClientDetails();
    this.officePhone = Utils.formatPhone(phone);
  },
};
</script>
<style lang="scss" scoped>
@import '@/scss/variables.scss';

// Form-specific color variables
$patient-header-bg-start: #133b54;
$patient-header-bg-end: #1a365d;
$patient-header-text: white;
$question-row-bg: #f8fafc;
$question-row-border: #e2e8f0;
$question-title-color: #374151;
$info-text-color: #6b7280;
$section-subtitle-color: #374151;

.patient-header {
  background: linear-gradient(180deg, $patient-header-bg-start 0%, $patient-header-bg-end 100%);
  color: $patient-header-text;
  padding: 16px;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 24px;
}

.form-section {
  margin-bottom: 32px;
}

.question-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: $question-row-bg;
  border: 1px solid $question-row-border;
  border-radius: 8px;
  
  @media (max-width: $breakpoint-mobile) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

.question-title {
  font-size: 16px;
  font-weight: 600;
  color: $question-title-color;
  margin: 0;
}

.info-text {
  color: $info-text-color;
  margin-bottom: 24px;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 600;
  color: $section-subtitle-color;
  margin: 0 0 16px 0;
}

.upload-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: $breakpoint-mobile) {
    grid-template-columns: 1fr;
  }
}

.upload-item {
  .upload-preview {
    margin-top: 12px;
    max-width: 200px;
    border-radius: 4px;
  }
}

.upload-btn {
  white-space: normal !important;
  height: auto !important;
  min-height: 36px !important;
  padding: 8px 16px !important;
  
  .upload-text-desktop {
    display: inline;
    
    @media (max-width: $breakpoint-mobile) {
      display: none;
    }
  }
  
  .upload-text-mobile {
    display: none;
    
    @media (max-width: $breakpoint-mobile) {
      display: inline;
    }
  }
}
</style>
